import React from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Wallet, Activity, ChefHat, Martini, ArrowUpRight } from "lucide-react";
import { Card } from "../components/ui/Primitives";
import { formatEUR } from "../utils/format";
import TemplateChecklistBanner from "../components/TemplateChecklistBanner";

function KpiCard({ icon: Icon, label, value, sub, accent = false, testId }) {
  return (
    <div
      data-testid={testId}
      className={`relative bg-bg-surface border border-white/10 rounded-xl p-5 flex flex-col gap-1 overflow-hidden lift hover:border-white/20 transition-colors group`}
    >
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-ink-secondary font-medium">{label}</div>
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${accent ? "bg-brand/15 text-brand" : "bg-white/5 text-ink-secondary"}`}>
          <Icon size={14} strokeWidth={1.6} />
        </div>
      </div>
      <div className="num text-2xl font-medium text-ink-primary tracking-tight mt-2">{value}</div>
      {sub && <div className="text-xs text-ink-muted">{sub}</div>}
      {accent && (
        <div className="absolute -bottom-10 -right-10 w-32 h-32 rounded-full bg-brand/10 blur-3xl opacity-50 group-hover:opacity-80 transition-opacity" />
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const planLabel = user?.plan && user.plan !== "none" ? user.plan : t("dashboard.no_plan");

  return (
    <div className="max-w-7xl mx-auto" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink-muted font-medium mb-1.5">
            {t("dashboard.welcome")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-ink-primary">
            {user?.business_name || user?.name || user?.email}
          </h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-secondary">
          <span className="w-1.5 h-1.5 rounded-full bg-ok animate-pulse" />
          Phase 1 · Auth + Zutaten
        </div>
      </div>

      <TemplateChecklistBanner />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <KpiCard
          icon={Wallet}
          label={t("dashboard.active_plan")}
          value={<span className="capitalize">{planLabel}</span>}
          sub={user?.demo_mode ? "Demo aktiv" : "—"}
          testId="kpi-plan"
        />
        <KpiCard
          icon={Sparkles}
          label={t("dashboard.ai_credits")}
          value={<span>{(user?.ai_credits ?? 0) + (user?.purchased_credits ?? 0)}</span>}
          sub={`Plan ${user?.ai_credits ?? 0} · Top-up ${user?.purchased_credits ?? 0}`}
          accent
          testId="kpi-credits"
        />
        <KpiCard
          icon={ChefHat}
          label={t("dashboard.food_cost")}
          value={<span className="text-ink-muted">—</span>}
          sub={`Ziel ${user?.target_food_cost?.toString().replace(".", ",") ?? "30,00"}%`}
          testId="kpi-foodcost"
        />
        <KpiCard
          icon={Martini}
          label={t("dashboard.beverage_cost")}
          value={<span className="text-ink-muted">—</span>}
          sub={`Ziel ${user?.target_pour_cost?.toString().replace(".", ",") ?? "20,00"}%`}
          testId="kpi-pourcost"
        />
      </div>

      <Card className="p-6 lg:p-7 mb-6">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-lg font-semibold text-ink-primary tracking-tight mb-1">
              {t("dashboard.wareneinsatz")}
            </h2>
            <p className="text-sm text-ink-secondary">{t("dashboard.this_month")} · {formatEUR(0)}</p>
          </div>
          <Activity size={16} className="text-ink-muted" strokeWidth={1.5} />
        </div>
        <div className="relative h-48 rounded-lg border border-white/5 bg-bg-void overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center text-xs text-ink-muted uppercase tracking-wider">
            {t("dashboard.coming_soon")}
          </div>
          <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_60%,rgba(59,130,246,0.06))]" />
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-6 lift hover:border-white/20">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-ink-primary tracking-tight">Nächster Schritt</h3>
            <ArrowUpRight size={14} className="text-brand" />
          </div>
          <p className="text-sm text-ink-secondary leading-relaxed">
            Legen Sie Ihre <a href="/ingredients" className="text-brand hover:text-brand-hover">Zutaten</a> an —
            damit sind Sie bereit für die Rezept- und Cocktail-Module, die in den nächsten Phasen freigeschaltet werden.
          </p>
        </Card>
        <Card className="p-6 lift hover:border-white/20">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-ink-primary tracking-tight">Roadmap</h3>
            <span className="text-[10px] uppercase tracking-wider text-ink-muted">Phase 2+</span>
          </div>
          <ul className="text-sm text-ink-secondary space-y-1.5">
            <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand" />Rezepte &amp; DB1/DB2</li>
            <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand" />Cocktails &amp; Flaschen-Kalkulator</li>
            <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand" />F&B Intelligence</li>
            <li className="flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-brand" />Stripe Abos &amp; Affiliate-Programm</li>
          </ul>
        </Card>
      </div>
    </div>
  );
}
